import { useState } from "react";
import PropTypes from "prop-types";
import { Button, Tooltip } from "antd";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import apiClient from "../../../helpers/apiClient";
import { useToast } from "../../../contexts/ToastContext";

/**
 * One-click "feature this car" star. Saves immediately (no Edit mode needed) and reports the new
 * value through `onChange`, so tables and detail pages can update their own copy of the car.
 */
function FeaturedToggle({ car, onChange, withLabel = false, size = "small" }) {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const [saving, setSaving] = useState(false);
    const featured = Boolean(car?.isFeatured);

    const toggle = async (event) => {
        event?.stopPropagation?.();
        if (!car?.id || saving) return;

        setSaving(true);
        try {
            const res = await apiClient.patch(`/api/car/${car.id}`, { isFeatured: !featured });
            if (!res?.data?.success) throw new Error(res?.data?.message);
            onChange?.({ ...car, isFeatured: !featured });
            toast.success({ message: !featured ? t("featuredOn") : t("featuredOff") });
        } catch (error) {
            toast.error({ message: t("featuredFailed"), description: error?.response?.data?.message });
        } finally {
            setSaving(false);
        }
    };

    const label = featured ? t("removeFromFeatured") : t("addToFeatured");

    return (
        <Tooltip title={label}>
            <Button
                type={featured && withLabel ? "primary" : "text"}
                ghost={featured && withLabel}
                shape={withLabel ? "round" : "default"}
                size={size}
                loading={saving}
                aria-pressed={featured}
                aria-label={label}
                icon={featured ? <StarFilled className="!text-[#f5a623]" /> : <StarOutlined className="!text-gray-400" />}
                onClick={toggle}>
                {withLabel ? (featured ? t("carDetail.featured") : t("addToFeatured")) : null}
            </Button>
        </Tooltip>
    );
}

FeaturedToggle.propTypes = {
    car: PropTypes.object,
    onChange: PropTypes.func,
    withLabel: PropTypes.bool,
    size: PropTypes.oneOf(["small", "middle", "large"]),
};

export default FeaturedToggle;
